import { useEffect, useState } from "react";

import { EPIC_EHR_WHITELABEL } from "../../constants/constants";
import notificationService from "../../service/NotificationService";

const TYPE_ORDER = ["warning", "informational", "promotional"];
const PORTAL_ALIAS_MAP = {
  livechat: ["livechat", "chat"],
  broadcast: ["broadcast"],
  logs: ["logs"],
  liveagent: ["liveagent", "chat-agent"],
};

const sortByType = (items = []) =>
  [...items].sort((a, b) => {
    const aIndex = TYPE_ORDER.indexOf(a?.notificationType);
    const bIndex = TYPE_ORDER.indexOf(b?.notificationType);

    return (aIndex === -1 ? TYPE_ORDER.length : aIndex) -
      (bIndex === -1 ? TYPE_ORDER.length : bIndex);
  });

const normalizeNotifications = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.notifications)) {
    return payload.notifications;
  }

  return [];
};

const normalizeRouteValue = (value = "") =>
  value.toString().trim().toLowerCase().replace(/^\/+/, "");

const expandAliases = (aliases = []) =>
  [...new Set(aliases.map(normalizeRouteValue).filter(Boolean))];

const matchesRoute = (page, routeAliases) => {
  if (!page) {
    return false;
  }

  const pageParts = page.split("/").filter(Boolean);

  return routeAliases.some((alias) => {
    if (!alias) {
      return false;
    }

    const aliasParts = alias.split("/").filter(Boolean);

    return (
      pageParts.includes(alias) ||
      aliasParts.includes(page) ||
      pageParts.some((part) => aliasParts.includes(part))
    );
  });
};

const getStorageKey = (storageKey) => storageKey || "notifications-public";

const getDismissedIds = (storageKey) => {
  try {
    return JSON.parse(localStorage.getItem(getStorageKey(storageKey)) || "[]");
  } catch (error) {
    return [];
  }
};

export const usePortalNotifications = ({
  pathAliases = [],
  displayLoginPage = false,
  practiceId,
  storageKey,
}) => {
  const [state, setState] = useState({
    banners: [],
    notifications: [],
  });

  useEffect(() => {
    let isMounted = true;

    const shouldShowNotification = (notification, dismissedIds) => {
      if (!notification || dismissedIds.includes(notification?.id)) {
        return false;
      }

      if (displayLoginPage) {
        return !!notification?.displayLoginPage;
      }

      if (!notification?.displayPortal?.trim()) {
        return false;
      }

      if (!EPIC_EHR_WHITELABEL) {
        return false;
      }

      const displayPages = notification?.displayPortal
        ? notification.displayPortal.split(",")
        : [];
      const normalizedPages = expandAliases(displayPages);
      const portalTypeAliases = expandAliases(
        PORTAL_ALIAS_MAP[normalizeRouteValue(notification?.portalType)] || []
      );
      const routeAliases = expandAliases([
        ...pathAliases,
        window.location.pathname,
        window.location.pathname.split("/")[1],
      ]);

      if (normalizedPages.includes("all")) {
        return true;
      }

      if (normalizedPages.length) {
        return normalizedPages.some((page) => matchesRoute(page, routeAliases));
      }

      if (portalTypeAliases.length) {
        return portalTypeAliases.some((page) =>
          matchesRoute(page, routeAliases)
        );
      }

      return false;
    };

    const fetchNotifications = async () => {
      try {
        const payload = await notificationService.getActiveNotifications(
          practiceId
        );
        const dismissedIds = getDismissedIds(storageKey);
        const notifications = normalizeNotifications(payload).filter(
          (notification) => shouldShowNotification(notification, dismissedIds)
        );

        if (isMounted) {
          setState({
            banners: sortByType(
              notifications.filter(
                (notification) => notification?.notificationStyle === "banner"
              )
            ),
            notifications: sortByType(
              notifications.filter(
                (notification) =>
                  notification?.notificationStyle === "notification"
              )
            ),
          });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            banners: [],
            notifications: [],
          });
        }
      }
    };

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, [displayLoginPage, pathAliases.join("|"), practiceId, storageKey]);

  const dismissNotification = (notificationId, listKey) => {
    const dismissedIds = getDismissedIds(storageKey);

    if (!dismissedIds.includes(notificationId)) {
      localStorage.setItem(
        getStorageKey(storageKey),
        JSON.stringify([...dismissedIds, notificationId])
      );
    }

    setState((st) => ({
      ...st,
      [listKey]: st[listKey].filter((item) => item?.id !== notificationId),
    }));
  };

  return {
    banners: state.banners,
    notifications: state.notifications,
    dismissBanner: (notificationId) =>
      dismissNotification(notificationId, "banners"),
    dismissPopup: (notificationId) =>
      dismissNotification(notificationId, "notifications"),
  };
};
